/**
 * StatCounter — Animated counting number for hero stats
 */

import { useEffect, useRef, useState } from "react";

interface StatCounterProps {
  value: number;
  label: string;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
}

export function StatCounter({
  value,
  label,
  decimals = 0,
  prefix = "",
  suffix = "",
  duration = 1000,
}: StatCounterProps) {
  // Start at the real value so SSR and first paint show correct number.
  // The animation counts from 0 → value on first intersection.
  const [displayValue, setDisplayValue] = useState(value);
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    // Reset animation state when value changes so the counter updates.
    hasAnimated.current = false;
    setDisplayValue(value);
  }, [value]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const startTime = performance.now();
          const step = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - (1 - progress) ** 3;
            setDisplayValue(eased * value);
            if (progress < 1) {
              requestAnimationFrame(step);
            }
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  const formatted =
    decimals > 0
      ? displayValue.toFixed(decimals)
      : Math.round(displayValue).toString();

  return (
    <div ref={ref} className="text-center min-w-0">
      <div className="font-display text-2xl sm:text-3xl font-bold text-foreground tabular-nums">
        {prefix}
        {formatted}
        {suffix}
      </div>
      <div className="text-xs text-muted-foreground mt-1 truncate">{label}</div>
    </div>
  );
}
